import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Outlet, useLocation } from 'react-router-dom';

import {
  Box,
  CircularProgress,
  Divider,
  ListItemText,
  Stack,
  useTheme,
} from '@mui/material';
import {
  Architecture,
  Apartment,
  Storefront,
} from '@mui/icons-material';

import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import { SideList, SideListItemButton } from '@frontend/features/common/components/SideList';
import { DataBox } from '@frontend/features/common/components/DataBox';
import { useGetSaleOrderDetails } from '@frontend/features/sale-orders/queries';
import { BackButton } from '@frontend/features/common/components/BackButton';
import { useBreakpoints } from '@frontend/features/common/hooks/useBreakpoints';

import { routes } from '../routes';
import { BookStatusType } from '@frontend/features/sale-orders/types';
import {
  MainContainer,
  SidebarOverlay,
  Sidebar,
  Title,
  Subtitle,
  Amount,
  ListItemIcon,
  ListItemTextPrimary,
  HotelListBox,
  Chip,
  RightContainer,
  RightOverlay,
} from '@frontend/features/sale-orders/styles';

export const SaleOrderView = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const { isMediumDown, isSmallDown } = useBreakpoints();
  const { data: details, isLoading } = useGetSaleOrderDetails({ id });

  const detailPath = routes.dashboard.saleOrder.detail.replace(':id', id || '');
  const paxesPath = routes.dashboard.saleOrder.passengers;
  const hotelsPath = routes.dashboard.saleOrder.item;

  const sidebarWidth = isSmallDown ? 280 : isMediumDown ? 320 : 360;
  const isCompact = isSmallDown || isMediumDown;

  return (
    <MainContainer gapValue={isCompact ? theme.spacing(4) : theme.spacing(8)}>
      {isLoading && <CircularProgress color="inherit" />}
      {details && (
        <SidebarOverlay sidebarWidth={sidebarWidth}
          className="overlayscrollbars-react"
          options={{ scrollbars: { autoHide: 'scroll', theme: 'os-theme-light' } }}
          defer
        >
          <Sidebar isCompact={isCompact}>
            <Box display="flex" alignItems="flex-start" width="100%" gap={1}>
              <IconAvatar 
                size={isCompact ? "small" : "medium"} 
                backgroundColor={theme.palette.divider}
              >
                <Storefront htmlColor={theme.palette.common.white} />
              </IconAvatar>
              <Stack
                direction="column"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={1}
                flex={1}
                minWidth={0}
              >
                <DataBox responsive sx={{ gap: 0, width: '100%' }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Title 
                      variant={isCompact ? "body1" : "subtitle2"} 
                      color="text.primary"
                    >
                      {t('saleOrder.view.title')}
                    </Title>
                  </Box>
                  <Subtitle 
                    variant="body2" 
                    isCompact={isCompact}
                  >
                    {t('saleOrder.view.subtitle', {record: details.record})}
                  </Subtitle>
                </DataBox>
                <Amount 
                  variant={isCompact ? "body2" : "body1"} 
                  color="text.primary"
                  isCompact={isCompact}
                >
                  {t('saleOrder.list.table.amount.value', { value: details.amount })}
                </Amount>
              </Stack>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Divider aria-hidden="true" textAlign="left" sx={{ mb: isCompact ? 2 : 3 }} />
              <SideList aria-label={t('saleOrder.view.sideBarTitle')} dense disablePadding>
                <SideListItemButton
                  compact={isCompact}
                  selected={Boolean(
                    id &&
                      location.pathname === `${detailPath}/${paxesPath}`|| location.pathname === detailPath
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${detailPath}/${paxesPath}`,
                    )
                  }
                >
                  <ListItemIcon isCompact={isCompact}>
                    <Architecture />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('saleOrder.view.passengers.title')} 
                    primaryTypographyProps={{
                      variant: isCompact ? 'body2' : 'body1',
                      component: ListItemTextPrimary,
                    }}
                  />
                </SideListItemButton>
                {
                  details.items.map((item, idx) => (
                    <SideListItemButton
                      key={idx}
                      compact={isCompact}
                      selected={Boolean(
                        id &&
                            location.pathname === `${detailPath}/${hotelsPath.replace(':itemId', item.id)}`
                      )}
                      onClick={() =>
                        id &&
                          navigate(
                            `${detailPath}/${hotelsPath.replace(':itemId', item.id)}`,
                          )
                      }
                    >
                      <ListItemIcon isCompact={isCompact}>
                        <Apartment />
                      </ListItemIcon>
                      <ListItemText
                        primary={(
                          <HotelListBox>
                            <Title
                              variant={isCompact ? "body2" : "body1"}
                              style={{ flex: 1, minWidth: 0 }}
                            >
                              {t('saleOrder.view.hotels.title', { name: item.metadata?.propertyName })}
                            </Title>
                            {item.bookings?.[0].status === BookStatusType.CANCELLED && (
                              <Chip
                                label={t('saleOrder.view.hotels.cancelled')}
                                color="error"
                                size={isCompact ? 'small' : 'small'}
                                isCompact={isCompact}
                              />
                            )}
                          </HotelListBox>
                        )}
                      />
                    </SideListItemButton>
                  ))
                }
              </SideList>
            </Box>
          </Sidebar>
        </SidebarOverlay>
      )}
      <RightContainer isCompact={isCompact}>
        <BackButton backRoute={routes.dashboard.saleOrder.root} skipHistory />
        <RightOverlay isCompact={isCompact}
          className="overlayscrollbars-react"
          options={{ scrollbars: { autoHide: 'scroll', theme: 'os-theme-light' } }}
          defer
        >
          <Outlet />
        </RightOverlay>
      </RightContainer>
    </MainContainer>
  );
};
